import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card } from '@mui/material';

import { EmptyBox } from '@europrocurement/flexy-components';
import {
    domainIcon,
    offerIcon,
    visibilityFullIcon,
    visibilityPartialIcon,
} from '@europrocurement/l2d-icons';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useOffreService } from '@europrocurement/l2d-domain';
import { OffresJsonldOffreRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';

import OfferForm from './Form';
import { offerFichePath, offerListPath } from './routes';
import FicheHeader, { type FicheSubtitleElement } from '../components/fiche/FicheHeader';
import buildCreationTitle from '../components/fiche/buildCreationTitle';
import LoadingScreen from '../forms/LoadingScreen';

const OFFER_SECTION_LABEL = 'Configuration des offres commerciales';

/**
 * Fiche d'une offre sur route dédiée (remplace l'édition/création en modale).
 * - `admin/offres/offre/new` : création.
 * - `admin/offres/offre/:id` : édition (l'offre est chargée par son identifiant).
 */
const OfferFiche: React.FunctionComponent = function () {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id || id === 'new';

    const { offerModel } = useOffreService();
    const { request } = useApiRequest<OffresJsonldOffreRead>();

    const [entity, setEntity] = useState<OffresJsonldOffreRead | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(!isCreate);
    const [isInError, setIsInError] = useState<boolean>(false);
    // Libellé saisi dans le formulaire (mode création), affiché dans l'en-tête.
    const [draftLabel, setDraftLabel] = useState<string>('');

    useEffect(() => {
        let active = true;
        if (!isCreate) {
            setIsLoading(true);
            setIsInError(false);

            request(offerModel.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => {
                    if (active) {
                        setEntity(result.data);
                    }
                },
                failureCallback: () => {
                    if (active) {
                        setIsInError(true);
                    }
                },
            }).finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        }

        return () => {
            active = false;
        };
    }, [id, isCreate, offerModel, request]);

    const goToList = useCallback(() => {
        navigate(offerListPath);
    }, [navigate]);

    // Après un enregistrement :
    // - en création : on ouvre la fiche du nouvel objet ;
    // - en édition : on reste sur la fiche et on rafraîchit l'entité
    //   (sans toggler le chargement, pour ne pas démonter le formulaire).
    const handleAfterSubmit = useCallback(
        (createdId?: number) => {
            if (isCreate) {
                if (createdId != null) {
                    navigate(offerFichePath(createdId));
                }
                return;
            }
            if (!id) {
                return;
            }
            request(offerModel.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => setEntity(result.data),
            });
        },
        [id, isCreate, navigate, offerModel, request],
    );

    const title = isCreate
        ? buildCreationTitle("Création d'une offre", draftLabel)
        : (entity?.libelle ?? 'Offre');

    const subtitleElements: FicheSubtitleElement[] =
        isCreate || !entity
            ? []
            : [
                  {
                      icon: domainIcon.props.icon,
                      label: `Domaine : ${entity.domaine?.libelle}`,
                  },
                  {
                      icon:
                          entity.visibilite === 'PARTIAL'
                              ? visibilityPartialIcon.props.icon
                              : visibilityFullIcon.props.icon,
                      label:
                          // eslint-disable-next-line no-nested-ternary
                          entity.visibilite === 'FULL'
                              ? 'Visibilité : complète'
                              : entity.visibilite === 'PARTIAL'
                                ? 'Visibilité : partielle'
                                : undefined,
                  },
              ];

    const renderContent = () => {
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isInError || (!isCreate && !entity)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '30vh',
                    }}
                >
                    <EmptyBox
                        text="Offre introuvable"
                        subText="Cette offre n'existe pas ou n'a pas pu être chargée."
                    />
                </Box>
            );
        }

        return (
            <OfferForm
                isCreate={isCreate}
                entity={entity}
                afterSubmit={handleAfterSubmit}
                onCancel={goToList}
                onLabelChange={setDraftLabel}
            />
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <FicheHeader
                icon={offerIcon}
                title={title}
                reference={isCreate ? undefined : entity?.id}
                subtitleElements={subtitleElements}
                sectionLabel={OFFER_SECTION_LABEL}
                listPath={offerListPath}
            />

            <Card sx={{ marginTop: 2, padding: 3 }}>{renderContent()}</Card>
        </Box>
    );
};

export default OfferFiche;
